import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { useInView } from "react-intersection-observer";

import SkeletonLoader from "../../Components/Skeleton/skeleton-loader";
import SegmentControl from "../../Components/Buttons/segment-control";
import TextField from "../../Components/Form Fields/text-field";
import CustomMap from "../../Components/Map";

import useDebounce from "../../CustomHooks/useDebounce";

import { useGetFidelNearestLocations } from "../../CustomHooks/Queries";

import { formatCurrency, formatNumber, getUserId } from "../../Utils/helpers";
import useDialogFocusManagement from "../../Utils/use-dialog-focus";

import { Close, Search, Store, Web } from "../../Assets/Icons";
import { AmericanExpress, ImagePlaceholder, Mastercard, VisaLogo } from "../../Assets/Images";
import { BackIcon } from "./nn-icons";

const BrandTypeOptions = [
  {
    label: "In-Store",
    value: "in-store",
    Icon: Store,
  },
  {
    label: "Online",
    value: "online",
    Icon: Web,
  },
];

const getBrandOfferType = (brand) => {
  const type = brand?.offer?.channel ?? brand?.offer?.offer_type ?? brand?.channel ?? brand?.offer_type ?? "";
  const normalized = String(type).toLowerCase().replace(/_/g, "-");
  if (["in-store", "instore"].includes(normalized)) return "in-store";
  if (normalized === "online") return "online";
  return null;
};

const matchesBrandType = (brand, selectedType) => {
  const offerType = getBrandOfferType(brand);
  const resolvedType = offerType ?? "in-store";
  return resolvedType === selectedType;
};

const BrandCardSkeleton = () => {
  return (
    <div className="brand-card skeleton">
      <SkeletonLoader className="image-placeholder-wrapper bg--loader-bg-dark-secondary" width="72px" height="72px">
        <ImagePlaceholder style={{ visibility: "visible" }} />
      </SkeletonLoader>
      <div className="brand-details">
        <SkeletonLoader
          className="border--rounded-lg bg--loader-bg-dark-secondary"
          width="100px"
          height="14px"
          sx={{ marginBottom: "14px" }}
        />
        <SkeletonLoader
          className="border--rounded-lg bg--loader-bg-dark-secondary"
          width="200px"
          height="12px"
          sx={{ marginBottom: "18px" }}
        />
        <SkeletonLoader className="border--rounded-lg bg--loader-bg-dark-secondary" width="120px" height="16px" />
      </div>
    </div>
  );
};

const BrandCardSkeletonLoader = () => Array.from(Array(5).keys()).map((_, idx) => <BrandCardSkeleton key={idx} />);

const getStakeVisaLabel = (offer) => {
  const svc = offer?.cb_reward_svc_card;
  const svd = offer?.cb_reward_svd_card;
  if (svc != null && svc !== "") return { type: "Credit", pct: svc };
  if (svd != null && svd !== "") return { type: "Debit", pct: svd };
  return { type: "Debit", pct: offer?.cb_reward_svd_card ?? 0 };
};

const BrandCard = ({ brand, onSelect, showDistance = true }) => {
  const stakeVisa = getStakeVisaLabel(brand?.offer);
  return (
    <button className="brand-card" onClick={onSelect}>
      <div className="brand-card-logo-wrap">
        {!!brand?.offer?.brand_url ? (
          <img className="brand-logo" src={brand?.offer?.brand_url} alt="" />
        ) : (
          <div className="image-placeholder-wrapper">
            <ImagePlaceholder />
          </div>
        )}
      </div>
      <div className="brand-card-content">
        <div className="brand-card-header">
          <span className="brand-name">{brand?.offer?.brand_name}</span>
          {showDistance && (
            <span className="brand-distance-to">{formatNumber(brand?.distance_in_miles, 2)} miles away</span>
          )}
        </div>
        <div className="brand-card-cashback-row">
          <div className="brand-offer">
            {brand?.offer?.cb_reward_external_card}% Cash Back
            {!!brand?.offer?.min_transaction_amount && (
              <span className="eta-to-nearest-outlet">
                ({formatCurrency(brand?.offer?.min_transaction_amount)?.replace(".00", "")} min)*
              </span>
            )}
          </div>
          {!!brand?.offer?.days_of_week && <span className="brand-open-on">{brand?.offer?.days_of_week}</span>}
        </div>
        <span className="brand-svd-offer">
          Stake {stakeVisa.type}: {stakeVisa.pct}% Cash Back
        </span>
      </div>
    </button>
  );
};

export const BrandOffers = ({setOfferId,filterByBrandName,filterByLocality,hideBrandTypeFilter = false}) => {
  const SEATED_PROVIDER_ID = "87655e32-ab33-4519-9539-4b2eb5933081";
  const residentId = getUserId();

  const [brandType, setBrandType] = useState("in-store");
  const [searchQuery, setSearchQuery] = useState("");

  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const [scrollRoot, setScrollRoot] = useState(null);
  const scrollContainerRef = useCallback((node) => {
    if (node) setScrollRoot(node);
  }, []);

  const {
    ref: infiniteScrollElementRef,
    inView: infiniteScrollElementInView,
  } = useInView({
    root: scrollRoot,
    rootMargin: "0px 0px 100px 0px",
    threshold: 0,
    triggerOnce: false,
  });

  const {
    isLoading: isLoadingFidelBrands,
    data: fidelBrands,
    fetchNextPage: fetchNextFidelBrands,
    hasNextPage: hasNextFidelBrands,
    isFetchingNextPage: isFetchingNextFidelBrands,
  } = useGetFidelNearestLocations({
    resident_id: residentId,
    contentProviderId: SEATED_PROVIDER_ID,
...(!hideBrandTypeFilter ? { brandType } : {}),

...(filterByBrandName && debouncedSearchQuery.trim()
      ? { brandName: debouncedSearchQuery.trim() }
      : {}),

    ...(filterByLocality && debouncedSearchQuery.trim()
      ? { location: debouncedSearchQuery.trim() }
      : {}),
  });

  useEffect(() => {
    if (infiniteScrollElementInView && hasNextFidelBrands) {
      fetchNextFidelBrands();
    }
  }, [infiniteScrollElementInView, hasNextFidelBrands, fetchNextFidelBrands]);

  return (
    <>
      {!hideBrandTypeFilter && (
        <SegmentControl
          value={brandType}
          onChange={(_, value) => setBrandType(value)}
          exclusive
          segmentOptions={BrandTypeOptions}
        />
      )}

      <div className="search-field-container">
        <TextField
          className="search-field "
          placeholder={
            filterByBrandName
              ? "Search by brands"
              : filterByLocality
              ? "Search by city or zip code"
              : ""
          }
          RightIcon={Search}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div ref={scrollContainerRef} className="brand-offers">
        {isLoadingFidelBrands ? (
          <BrandCardSkeletonLoader />
        ) : (() => {
          const filteredPages =
            fidelBrands?.pages?.map((brandPage) => {
              let brands = brandPage?.data?.data ?? [];

              if (!hideBrandTypeFilter) {
                brands = brands.filter((brand) =>
                  matchesBrandType(brand, brandType)
                );
              }

              return brands;
            }) ?? [];

          const hasAnyFiltered = filteredPages.some(
            (arr) => arr?.length > 0
          );

          if (!hasAnyFiltered) {
            return (
              <div className="no-offers-found">
                {debouncedSearchQuery
                  ? `No brands found for "${debouncedSearchQuery}"`
                  : "No offers found"}
              </div>
            );
          }

          return (
            <>
              {filteredPages.map((filteredBrands, pageIdx) => (
                <React.Fragment key={pageIdx}>
                  {filteredBrands.map((brand, idx) => (
                    <BrandCard
                      key={`${brand?.offer_id ?? idx}-${idx}`}
                      brand={brand}
                      showDistance={hideBrandTypeFilter || brandType !== "online"}
                      onSelect={() => setOfferId(brand?.offer_id)}
                    />
                  ))}
                </React.Fragment>
              ))}

              <div
                ref={infiniteScrollElementRef}
                style={{ minHeight: 1 }}
              >
                {isFetchingNextFidelBrands && (
                  <BrandCardSkeletonLoader />
                )}
              </div>
            </>
          );
        })()}
      </div>
    </>
  );
};

const getLocationAddress = (brand) => {
  const o = brand?.offer;
  const loc = brand?.location ?? o?.location ?? brand;
  const addr =
    brand?.address ||
    brand?.street_address ||
    brand?.address_line_1 ||
    brand?.address1 ||
    o?.address ||
    o?.street_address ||
    o?.location_address ||
    o?.address_line_1 ||
    o?.address1 ||
    loc?.address ||
    loc?.street_address ||
    loc?.address_line_1;
  const city = brand?.city ?? o?.city ?? loc?.city ?? "";
  const state = brand?.state ?? o?.state ?? loc?.state ?? brand?.region ?? o?.region ?? "";
  const zip =
    brand?.postal_code ??
    brand?.postcode ??
    brand?.zip ??
    o?.postal_code ??
    o?.postcode ??
    o?.zip ??
    loc?.postal_code ??
    loc?.postcode ??
    "";
  const fullAddr = brand?.formatted_address ?? o?.formatted_address ?? loc?.formatted_address;
  if (fullAddr) return fullAddr;
  const parts = [addr, city, state, zip].filter(Boolean);
  return parts.length ? parts.join(", ") : "Online";
};

export const BrandDetails = ({ offerId, residentId, overlay = false, panel = false, embedMap = true, onClose }) => {
  const { isLoading: isLoadingFidelBrands, data: fidelBrands } = useGetFidelNearestLocations({
    offerId,
    resident_id: residentId,
  });

  const offer = useMemo(() => {
    return fidelBrands?.pages?.reduce((matchedOffer, brandPage) => {
      if (!!matchedOffer) return matchedOffer;
      const found = brandPage?.data?.data?.find((brand) => brand?.offer_id === offerId);
      return found ?? matchedOffer;
    }, null);
  }, [fidelBrands, offerId]);

  const stakeVisa = useMemo(() => getStakeVisaLabel(offer?.offer), [offer?.offer]);
  const locationAddressFromApi = useMemo(() => getLocationAddress(offer), [offer]);
  const isOnlineOffer = useMemo(
    () => getBrandOfferType(offer) === "online" || getLocationAddress(offer) === "Online",
    [offer]
  );

  const [geocodedAddress, setGeocodedAddress] = useState(null);


  const locationAddress = geocodedAddress ?? locationAddressFromApi;

  const brandTitleId = `brand-details-title-${offerId}`;
  const dialogRef = useRef(null);
  const isDialog = overlay || panel;
  useDialogFocusManagement({
    isOpen: isDialog && !isLoadingFidelBrands,
    dialogRef,
    closeSelector: ".brand-details-close",
    onEscape: typeof onClose === "function" ? onClose : undefined,
  });
  return isLoadingFidelBrands ? (
    <div className="loading-content">
      <CircularProgress />
    </div>
  ) : (
    <div
      ref={isDialog ? dialogRef : null}
      className={`detailed-brand-card ${overlay ? "overlay" : ""} ${panel ? "panel" : ""}`}
      {...(isDialog
        ? { role: "dialog", "aria-modal": "true", "aria-labelledby": brandTitleId }
        : {})}
    >
      {!!onClose && (
        <div className="brand-details-header">
          <button type="button" className="brand-details-close" onClick={onClose} aria-label="Close">
            <Close />
          </button>
        </div>
      )}
      <div className="brand-details">
        <div className="brand-info">
          <div className="brand-name" id={brandTitleId}>{offer?.offer?.brand_name}</div>
          <div className="brand-offer">
            {offer?.offer?.cb_reward_external_card}% Cash Back
            {!!offer?.offer?.max_reward_amount && (
              <span className="eta-to-nearest-outlet">(up to {formatCurrency(offer?.offer?.max_reward_amount)})</span>
            )}{" "}
            {!!offer?.offer?.min_transaction_amount && (
              <span className="eta-to-nearest-outlet">
                ({formatCurrency(offer?.offer?.min_transaction_amount)?.replace(".00", "")} min)*
              </span>
            )}
          </div>
          <span className="brand-svd-offer">
            Stake {stakeVisa.type}: {stakeVisa.pct}% Cash Back
          </span>
        </div>
        <div className="brand-logo-wrap">
          {!!offer?.offer?.brand_url ? (
            <img className="brand-logo" src={offer?.offer?.brand_url} alt="" />
          ) : (
            <div className="image-placeholder-wrapper">
              <ImagePlaceholder />
            </div>
          )}
        </div>
      </div>
      <div className="offer-details-section mt-5">
        <div className="section-title">Offer details</div>
        {!!offer?.offer?.end_date && (
          <div className="offer-detail">
            <div className="detail-title">Expires on</div>
            <div className="detail-value">{moment(offer?.offer?.end_date).format("MMM D, YYYY")}</div>
          </div>
        )}
        <div className="offer-detail">
          <div className="detail-title">Location</div>
          <div className="detail-value">{locationAddress}</div>
        </div>
        <div className="offer-detail">
          <div className="detail-title">Networks</div>
          <div className="detail-value">
            {offer?.offer?.network?.includes("visa") && <VisaLogo />}
            {offer?.offer?.network?.includes("mastercard") && <Mastercard />}
            {offer?.offer?.network?.includes("amex") && <AmericanExpress />}
          </div>
        </div>
      </div>
      {!overlay && !panel && embedMap && !isOnlineOffer && (
  <div className="mobile-only-map">
    <CustomMap
      latitude={Number(offer?.latitude ?? offer?.offer?.latitude)}
      longitude={Number(offer?.longitude ?? offer?.offer?.longitude)}
      highlightSingle={
        !!(offer?.latitude ?? offer?.offer?.latitude)
      }
    />
  </div>
)}
      {!!offer?.offer?.terms_and_conditions ? (
        <div className="offer-description-section mt-5">
          <div className="section-title mb-2">Terms & Conditions</div>
          <div className="description">{offer?.offer?.terms_and_conditions}</div>
        </div>
      ) : (
        <div className="offer-description-section mt-5">
          <div className="section-title mb-2">Terms & Conditions</div>
          <div className="description">
            This offer is provided by the participating merchant, not by Stake. Stake is not responsible for the
            products, services, or rewards offered and makes no guarantees about their availability, quality, or
            fulfillment. Cash back is subject to the merchant's terms and may change or end at any time. Offers are valid
            only while active and may be limited by region, eligibility, and usage. To earn cash back, complete your
            purchase through the linked offer using an eligible card. Cash back may take time to appear and is not
            guaranteed if a transaction is canceled, returned, or does not meet the offer requirements. For questions
            about a purchase, contact the merchant directly.
          </div>
        </div>
      )}
    </div>
  );
};



function NeighborhoodNetworkBrandsAndOffers() {
  const residentId = getUserId()
  const [offerId, setOfferId] = useState(null);
  const navigate = useNavigate();

  return (
    <div className="nn-page brands-and-offers anim-ready">
      <section>
        <div className="max-width-container">
          <div className="nn-content-card">
            <div className="nn-card-header">
              <button
                type="button"
                className="nn-back-btn"
                onClick={() => (!offerId ? navigate(-1) : setOfferId(null))}
                aria-label="Go back"
              >
                <BackIcon />
              </button>
              <h1>{!offerId ? "Brands & Offers" : "Offer Details"}</h1>
              <span className="nn-header-spacer" />
            </div>
            {!offerId ? (
              <BrandOffers filterByBrandName setOfferId={setOfferId} />
            ) : (
              <BrandDetails offerId={offerId} residentId={residentId} embedMap={true} />
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

export default NeighborhoodNetworkBrandsAndOffers;
